/**
 * Pricing Page - Integrated with Backend API
 * Fetches all pricing data from backend endpoints
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Check,
  X,
  ArrowRight,
  MessageSquare,
  Zap,
  HelpCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { Pricing } from "../../components/ui/pricing-table";
import { useAllPricingData } from "../../hooks/usePricing";
import { PricingCardSkeleton } from "../../components/Skeletons/CardSkeleton";
import { ErrorState, EmptyState } from "../../components/States/ErrorState";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const PricingHero = ({ settings }) => (
  <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-300/15 rounded-full blur-[120px]" />
      <div className="absolute top-20 right-[10%] w-72 h-72 bg-gradient-to-br from-violet-200/25 to-purple-300/15 animate-morph-blob blur-xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Left — text */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="text-center lg:text-left"
        >
          <motion.h1
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
          >
            {settings?.heroTitle && (
              <>
                {settings.heroTitle.split(".")[0]}.{" "}
                <span className="text-gradient">
                  {settings.heroTitle.split(".")[1]}.
                </span>
              </>
            )}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-2xl lg:max-w-none"
          >
            {settings?.heroDescription}
          </motion.p>
        </motion.div>

        {/* Right — decorative glass card with image (lg only) */}
        {settings?.heroImageUrl && (
          <motion.div
            initial={{ opacity: 0, x: 40, filter: "blur(8px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative glass-strong rounded-2xl p-3 shadow-apple-xl border border-white/40 overflow-hidden">
              <img
                src={settings.heroImageUrl}
                alt="Pricing & finance analytics"
                className="rounded-xl w-full h-56 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent rounded-2xl" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center shadow-lg">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">
                    Smart Pricing
                  </p>
                  <p className="text-xs text-slate-500">
                    Plans that grow with you
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  </section>
);

const PricingToggle = ({ isAnnual, setIsAnnual, discountCopy }) => (
  <div className="flex items-center justify-center gap-3 mt-10">
    <span
      className={`text-sm font-medium whitespace-nowrap ${!isAnnual ? "text-slate-900" : "text-slate-400"}`}
    >
      Monthly
    </span>
    <button
      type="button"
      onClick={() => setIsAnnual(!isAnnual)}
      role="switch"
      aria-checked={isAnnual}
      aria-label="Toggle billing period"
      className={`relative inline-flex h-6 min-h-0 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 ${isAnnual ? "bg-violet-600" : "bg-slate-300"}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ${isAnnual ? "translate-x-5" : "translate-x-0"}`}
      />
    </button>
    <span
      className={`text-sm font-medium whitespace-nowrap ${isAnnual ? "text-slate-900" : "text-slate-400"}`}
    >
      Annual{" "}
      <span className="text-violet-600 font-semibold text-xs ml-1">
        {discountCopy}
      </span>
    </span>
  </div>
);

const PricingCards = ({ isAnnual, pricingPlans, settings }) => (
  <section>
    <Pricing
      tiers={pricingPlans}
      isAnnual={isAnnual}
      footerTitle={settings?.footerTitle}
      footerDescription={settings?.footerDescription}
      footerEligibility={settings?.footerEligibility}
      footerSteps={settings?.footerSteps}
      footerButtonText={settings?.footerButtonText}
      footerButtonLink={settings?.footerButtonLink}
    />

    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-center text-sm text-slate-400">
        {settings?.footerNote}
      </p>

      {/* Trust badges */}
      {settings?.trustBadges && settings.trustBadges.length > 0 && (
        <div className="mt-10 flex flex-wrap justify-center gap-6">
          {settings.trustBadges.map((badge) => (
            <div
              key={badge.label}
              className="flex items-center gap-2 text-sm text-slate-500"
            >
              <span>{badge.icon}</span>
              <span>{badge.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Trust strip */}
      {settings?.clientLogos && settings.clientLogos.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 glass-strong rounded-2xl p-6 sm:p-8 shadow-apple max-w-4xl mx-auto text-center"
        >
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">
            {settings?.clientLogosLabel}
          </p>
          <div className="flex justify-center items-center gap-8 flex-wrap opacity-40">
            {settings.clientLogos.map((logo, idx) => (
              <img
                key={idx}
                src={logo}
                alt="Client"
                className="h-8 rounded object-contain grayscale"
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  </section>
);

const FAQSection = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <section className="py-16 sm:py-24 bg-violet-tint">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center"
        >
          <motion.span
            variants={fadeUp}
            className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
          >
            FAQ
          </motion.span>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="mt-3 sm:mt-4 text-2xl sm:text-3xl font-bold text-slate-900"
          >
            Frequently asked questions
          </motion.h2>
        </motion.div>

        <div className="mt-12 space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={faq._id}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="glass rounded-xl overflow-hidden shadow-apple"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
              >
                <span className="text-sm font-semibold text-slate-800">
                  {faq.question}
                </span>
                {openIndex === i ? (
                  <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-4">
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingCTA = ({ settings }) => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 sm:p-12 md:p-20 text-center shadow-apple-xl">
        <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl animate-float-orb" />
        <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl animate-float-orb-reverse" />
        <div className="absolute inset-0 animate-rotate-slow opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-white/20 rounded-full" />
          <div className="absolute bottom-1/3 right-1/3 w-20 h-20 border border-white/10 rounded-full" />
        </div>
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-5">
              {settings?.ctaTitle}
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8 sm:mb-10">
              {settings?.ctaDescription}
            </p>
            <Link
              to={settings?.ctaButtonLink || "/contact"}
              className="btn-shine group inline-flex items-center gap-3 px-8 py-4 bg-white text-violet-700 font-semibold rounded-2xl hover:bg-violet-50 hover:scale-[1.02] transition-all duration-300 shadow-apple-lg"
            >
              {settings?.ctaButtonText}{" "}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

const PricingPage = ({ previewData = null, embedded = false }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  // Fetch all pricing data using React Query hook
  const {
    plans: pricingPlans = [],
    faqs = [],
    settings,
    isLoading: loading,
    error,
  } = useAllPricingData();

  // If previewData is provided, use it instead
  if (previewData) {
    return (
      <div
        className={`${embedded ? "bg-[#FAFAFE]" : "min-h-screen bg-[#FAFAFE] pb-24 md:pb-0"}`}
      >
        {!embedded && <Header />}
        {previewData.settings && (
          <PricingHero settings={previewData.settings} />
        )}
        <div>
          {previewData.settings && (
            <PricingToggle
              isAnnual={isAnnual}
              setIsAnnual={setIsAnnual}
              discountCopy={previewData.settings.discountCopy}
            />
          )}
        </div>
        {previewData.pricingPlans?.length > 0 && (
          <PricingCards
            isAnnual={isAnnual}
            pricingPlans={previewData.pricingPlans}
            settings={previewData.settings}
          />
        )}
        {previewData.faqs?.length > 0 && <FAQSection faqs={previewData.faqs} />}
        {previewData.settings && <PricingCTA settings={previewData.settings} />}
        {!embedded && <Footer />}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0 flex flex-col">
        {!embedded && <Header />}
        <div className="flex-1 pt-24 sm:pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="h-20 bg-slate-200/50 rounded-2xl mb-12 animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <PricingCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0 flex flex-col">
        {!embedded && <Header />}
        <div className="flex-1 pt-24 sm:pt-32 pb-16">
          <ErrorState
            title="Failed to load pricing"
            description="We couldn't load the pricing information. Please try again or contact us."
            onRetry={() => window.location.reload()}
          />
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  return (
    <div
      className={`${embedded ? "bg-[#FAFAFE]" : "min-h-screen bg-[#FAFAFE] pb-24 md:pb-0"}`}
    >
      {!embedded && <Header />}
      {settings && <PricingHero settings={settings} />}
      <div>
        {settings && (
          <PricingToggle
            isAnnual={isAnnual}
            setIsAnnual={setIsAnnual}
            discountCopy={settings.discountCopy}
          />
        )}
      </div>
      {pricingPlans.length > 0 && (
        <PricingCards
          isAnnual={isAnnual}
          pricingPlans={pricingPlans}
          settings={settings}
        />
      )}
      {faqs.length > 0 && <FAQSection faqs={faqs} />}
      {settings && <PricingCTA settings={settings} />}
      {!embedded && <Footer />}
    </div>
  );
};

export default PricingPage;
